import { MantineProvider } from '@mantine/core';
import _sum from 'lodash/sum';
import type { DataTableColumn } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';
import { useCallback, useMemo } from 'react';

import type {
  EstimationModelItemGroupResponse,
  EstimationModelResponse,
} from '@/api/schema/data-contracts';
import IconExpand from '@/assets/Icons/IconExpand';
import { FormattedNumber } from '@/components/atoms/FormattedNumber';
import { Stack } from '@/components/atoms/Stack';
import { stripHtml } from '@/helpers/estimation';
import { useMessages } from '@/hooks/messages';
import type {
  CostEstimationItemsTableRecord,
  CostModel,
  ResourceEstimationItemsTableRecord,
} from '@/pages/estimation/detail/model';
import { sortCostTypes, sortExpressions } from '@/utils/sorting';

import { useHierarchicalSearch } from '../hooks/useHierarchicalSearch';
import { managementReportSummaryTableMessageDefinitions } from '../ManagementReportSummaryTable/messages';

export interface HierarchicalTableRow {
  id: string;
  reference: string;
  name: string;
  description?: string;
  isParent: boolean;
  level: number;
  estimationItemId: string;
  resourceEstimationItem?: ResourceEstimationItemsTableRecord;
  costEstimationItem?: CostEstimationItemsTableRecord;
  children?: HierarchicalTableRow[];
}

interface TableConfig<T> {
  items: T[];
  getChildrenByParent: (parentId: string | undefined) => T[];
  createRowFromItem: (
    item: T,
    counter: number,
    subCounter?: number,
    level?: number,
  ) => HierarchicalTableRow;
  getEstimationItemForVisibility: (row: HierarchicalTableRow) => T | undefined;
  defaultGroupName: string;
  showEffortsColumn: boolean;
  getEffortValue?: (row: HierarchicalTableRow) => number;
  emptyStateMessage: string;
}

export interface HierarchicalManagementReportTableProps<T> {
  config: TableConfig<T>;
  costs?: CostModel;
  estimationModel: EstimationModelResponse;
  assumptions?: Array<{
    id: string;
    estimationItemId: string;
    description: string;
    isValid: boolean;
  }>;
  searchText?: string;
  hiddenCostTypeColumns?: string[];
}

export interface RowAssumption {
  id: string;
  estimationItemId: string;
  description: string;
  isValid: boolean;
}

/**
 * The assumptions a report row is answerable for: its own, plus - for a phase row - every
 * descendant's.
 *
 * EWM-998. The assumptions cell used to match strictly on the row's own estimation item id.
 * Assumptions are materialized on the work packages (the advisory's impact table is M:N over WBS
 * items), and the Resource Cost Breakdown Detail table renders children only while their parent is
 * expanded - which it is not by default. So every collapsed phase read "no assumptions" on an
 * estimation that had them, while the Summary table above showed them, because it rolls descendants
 * up onto the phase before handing them over. Reported from TEST on 2026-08-25 against estimation
 * 19d15511-534e-48ed-f314-08df02b022bd.
 *
 * An expanded child row still answers for itself only - the phase is the summary, the child is the
 * detail.
 */
/**
 * EWM-1135: assumptions keyed by the item they hang off, built once per data change.
 *
 * `collectRowAssumptions` used to run `assumptions.filter(...)` over the whole list for every
 * rendered row, which is O(rows x assumptions) per render - 64 ms on a 4k-item estimation.
 * `orderById` keeps the original array position so a phase row's assumptions still come back in
 * input order, which the de-duplication in the rendered cell depends on.
 */
export interface RowAssumptionIndex {
  byItemId: ReadonlyMap<string, RowAssumption[]>;
  orderById: ReadonlyMap<string, number>;
}

export const buildRowAssumptionIndex = (assumptions: RowAssumption[]): RowAssumptionIndex => {
  const byItemId = new Map<string, RowAssumption[]>();
  const orderById = new Map<string, number>();

  assumptions.forEach((assumption, position) => {
    orderById.set(assumption.id, position);
    const existing = byItemId.get(assumption.estimationItemId);
    if (existing) {
      existing.push(assumption);
    } else {
      byItemId.set(assumption.estimationItemId, [assumption]);
    }
  });

  return { byItemId, orderById };
};

export const collectRowAssumptions = <T extends { id: string }>(
  row: Pick<HierarchicalTableRow, 'isParent' | 'estimationItemId'>,
  index: RowAssumptionIndex,
  getChildrenByParent: (parentId: string | undefined) => T[],
): RowAssumption[] => {
  if (!row.isParent) {
    return index.byItemId.get(row.estimationItemId) ?? [];
  }

  const ids = new Set<string>([row.estimationItemId]);
  const queue = [row.estimationItemId];
  while (queue.length > 0) {
    // Guarded by ids, so a parent cycle in the data cannot spin here.
    const parentId = queue.pop()!;
    getChildrenByParent(parentId).forEach((child) => {
      if (!ids.has(child.id)) {
        ids.add(child.id);
        queue.push(child.id);
      }
    });
  }

  const collected: RowAssumption[] = [];
  for (const id of ids) {
    const forItem = index.byItemId.get(id);
    if (forItem) {
      collected.push(...forItem);
    }
  }

  // The subtree is walked breadth-first, so restore the caller's original ordering.
  return collected.sort((a, b) => index.orderById.get(a.id)! - index.orderById.get(b.id)!);
};

const HierarchicalManagementReportTable = <
  T extends { id: string; estimationModelGroupId: string; name: string; description?: string },
>({
  config,
  costs,
  estimationModel,
  assumptions = [],
  searchText = '',
  hiddenCostTypeColumns = [],
}: HierarchicalManagementReportTableProps<T>) => {
  const messages = useMessages(managementReportSummaryTableMessageDefinitions);

  const { expandedItems, toggleItemExpansion, shouldShowItem } = useHierarchicalSearch({
    items: config.items,
    searchText,
    getChildrenByParent: config.getChildrenByParent,
  });

  // Group items by estimationModelGroupId
  const itemsByGroupId = useMemo(() => {
    const grouped: Record<string, T[]> = {};
    config.items.forEach((item) => {
      const groupId = item.estimationModelGroupId;
      if (!grouped[groupId]) {
        grouped[groupId] = [];
      }
      grouped[groupId].push(item);
    });
    return grouped;
  }, [config.items]);

  const getEstimationModelItemGroup = useCallback(
    (estimationModelGroupId: string) =>
      estimationModel.itemGroups.find((x) => x.id === estimationModelGroupId),
    [estimationModel],
  );

  // Create hierarchical data structure
  const createHierarchicalData = useCallback(
    (groupItems: T[]) => {
      const rows: HierarchicalTableRow[] = [];
      let itemCounter = 1;

      groupItems.forEach((item) => {
        const children = config.getChildrenByParent(item.id);
        let subItemCounter = 1;

        // Add parent item
        const parentRow = config.createRowFromItem(item, itemCounter, undefined, 0);
        parentRow.isParent = true;
        parentRow.children = [];

        // Add sub-items only if parent is expanded
        if (children.length > 0 && expandedItems.has(item.id)) {
          children.forEach((child) => {
            const childRow = config.createRowFromItem(child, itemCounter, subItemCounter, 1);
            childRow.isParent = false;
            parentRow.children?.push(childRow);
            subItemCounter++;
          });
        }

        rows.push(parentRow);
        if (expandedItems.has(item.id) && parentRow.children.length > 0) {
          rows.push(...parentRow.children);
        }
        itemCounter++;
      });

      return rows;
    },
    [config, expandedItems],
  );

  const assumptionIndex = useMemo(() => buildRowAssumptionIndex(assumptions), [assumptions]);

  const assumptionsForRow = useCallback(
    (row: HierarchicalTableRow) =>
      collectRowAssumptions(row, assumptionIndex, config.getChildrenByParent),
    [assumptionIndex, config],
  );

  // Create cost type columns
  const costsColumns: DataTableColumn<HierarchicalTableRow>[] = sortCostTypes(
    estimationModel.costTypes,
    estimationModel.costTypeGroups,
  )
    .filter((costType) => !hiddenCostTypeColumns.includes(costType.id))
    .map((costType) => ({
      accessor: `costType${costType.id}`,
      title: costType.name,
      noWrap: true,
      width: 200,
      textAlign: 'right',
      hidden: !costs,
      titleClassName: 'whitespace-break-spaces',
      render: (row: HierarchicalTableRow) => {
        if (!costs) {
          return undefined;
        }

        let calculatedCost = 0;

        // Calculate cost for resource estimation items
        if (row.resourceEstimationItem) {
          const resourcesForCostType = row.resourceEstimationItem.resources?.filter((resource) =>
            costs.resourceTypeCosts.find(
              (resourceTypeCost) =>
                resourceTypeCost.costTypeId === costType.id &&
                resourceTypeCost.resourceTypeId === resource.resourceTypeId,
            ),
          );
          calculatedCost = resourcesForCostType
            ? _sum(
                resourcesForCostType.map(
                  (resource) =>
                    (resource.value ?? 0) *
                    (costs.resourceTypeCosts.find(
                      (resourceTypeCost) =>
                        resourceTypeCost.costTypeId === costType.id &&
                        resourceTypeCost.resourceTypeId === resource.resourceTypeId,
                    )?.price ?? 0),
                ),
              )
            : 0;
        }

        // Calculate cost for cost estimation items
        if (row.costEstimationItem) {
          calculatedCost = _sum(
            row.costEstimationItem.estimationItemCosts
              .filter((itemCost) => itemCost.costTypeId === costType.id)
              .map((itemCost) => itemCost.price ?? 0),
          );
        }

        return <FormattedNumber value={calculatedCost} />;
      },
    }));

  const columns = (
    estimationModelGroup: EstimationModelItemGroupResponse,
  ): DataTableColumn<HierarchicalTableRow>[] => {
    const baseColumns: DataTableColumn<HierarchicalTableRow>[] = [
      {
        accessor: 'reference',
        title: '#',
        sortable: false,
        width: 80,
        render: (row: HierarchicalTableRow) => (
          <div className={`flex items-center ${row.level > 0 ? 'ml-4' : ''}`}>
            {row.isParent && (
              <button
                onClick={() => toggleItemExpansion(row.estimationItemId)}
                className='mr-2 flex h-5 w-5 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                title={expandedItems.has(row.estimationItemId) ? 'Collapse' : 'Expand'}
              >
                <IconExpand expanded={expandedItems.has(row.estimationItemId)} />
              </button>
            )}
            <span>{row.reference}</span>
          </div>
        ),
      },
      {
        accessor: 'name',
        title: estimationModelGroup?.name ?? config.defaultGroupName,
        sortable: false,
        noWrap: false,
        width: 300,
        render: (row: HierarchicalTableRow) => (
          <div
            className={`${row.level > 0 ? 'ml-4' : ''} ${row.isParent ? 'font-semibold' : ''} break-words whitespace-normal`}
          >
            {row.name}
          </div>
        ),
      },
      {
        accessor: 'buttons_placeholder',
        title: '',
        // Direct Cost mode has no Total Efforts column, so absorb its width here to keep
        // alignment with DirectCostSummaryTable below (buttons_placeholder 250 + assumptions 50).
        width: config.showEffortsColumn ? 150 : 300,
        render: () => null,
      },
    ];

    if (config.showEffortsColumn) {
      baseColumns.push({
        accessor: 'totalEfforts',
        title: messages.totalEfforts(),
        noWrap: true,
        width: 150,
        textAlign: 'right',
        render: (row: HierarchicalTableRow) => {
          const value = config.getEffortValue ? config.getEffortValue(row) : 0;
          return <FormattedNumber value={value} />;
        },
      });
    }

    // Add cost columns
    const costAndTotalColumns: DataTableColumn<HierarchicalTableRow>[] = [
      ...costsColumns,
      {
        accessor: 'costSum',
        title: messages.subtotal(),
        noWrap: true,
        width: 200,
        textAlign: 'right',
        titleClassName: 'whitespace-break-spaces',
        render: (row: HierarchicalTableRow) => {
          if (!costs) {
            return undefined;
          }

          let totalCost = 0;

          // Calculate total cost for resource estimation items
          if (row.resourceEstimationItem) {
            totalCost = row.resourceEstimationItem.resources
              ? _sum(
                  row.resourceEstimationItem.resources.map(
                    (resource) =>
                      (resource.value ?? 0) *
                      (costs.resourceTypeCosts.find(
                        (resourceTypeCost) =>
                          resourceTypeCost.resourceTypeId === resource.resourceTypeId,
                      )?.price ?? 0),
                  ),
                )
              : 0;
          }

          // Calculate total cost for cost estimation items
          if (row.costEstimationItem) {
            totalCost = _sum(
              row.costEstimationItem.estimationItemCosts.map((itemCost) => itemCost.price ?? 0),
            );
          }

          return <FormattedNumber value={totalCost} />;
        },
      },
      {
        accessor: 'assumptions',
        title: messages.assumptions(),
        noWrap: false,
        width: 300,
        textAlign: 'left',
        render: (row: HierarchicalTableRow) => {
          const itemAssumptions = assumptionsForRow(row)
            .map((assumption) => ({
              id: assumption.id,
              text: stripHtml(assumption.description || '').trim(),
            }))
            .filter((item) => item.text.length > 0)
            // The same assumption is materialized once per item it impacts, so a phase whose
            // packages share one would otherwise list it once per package.
            .filter((item, i, all) => all.findIndex((other) => other.text === item.text) === i);

          if (itemAssumptions.length === 0) {
            return <span className='text-gray-400 italic'>{messages.noAssumptions()}</span>;
          }

          return (
            <div
              className='line-clamp-3 text-wrap break-words'
              title={itemAssumptions.map((a) => a.text).join('\n')}
            >
              <ol className='list-inside list-decimal space-y-1 text-sm'>
                {itemAssumptions.map((assumption) => (
                  <li key={assumption.id} className='break-words'>
                    {assumption.text}
                  </li>
                ))}
              </ol>
            </div>
          );
        },
      },
    ];

    return [...baseColumns, ...costAndTotalColumns];
  };

  if (Object.keys(itemsByGroupId).length === 0) {
    return (
      <div className='mt-4 w-full overflow-x-auto rounded-xl'>
        <p>{config.emptyStateMessage}</p>
      </div>
    );
  }

  return (
    <div>
      <Stack gap={10}>
        {Object.keys(itemsByGroupId)
          .map(
            (estimationModelItemGroupId) =>
              getEstimationModelItemGroup(estimationModelItemGroupId)!,
          )
          .filter(Boolean)
          .sort(sortExpressions.byOrder)
          .map((estimationModelGroup) => {
            const groupItems = itemsByGroupId[estimationModelGroup.id];
            const hierarchicalData = createHierarchicalData(groupItems);

            // Skip groups with no items
            if (hierarchicalData.length === 0) {
              return null;
            }

            return (
              <div key={estimationModelGroup.id} className='mt-4 w-full overflow-x-auto rounded-xl'>
                <MantineProvider>
                  <div className='relative'>
                    <div className='pointer-events-none absolute top-0 right-0 z-[5] block h-full w-8 bg-gradient-to-l from-white/80 to-transparent dark:from-black/80' />
                    <DataTable<HierarchicalTableRow>
                      highlightOnHover
                      withTableBorder
                      className='table-hover rounded-xl p-2 whitespace-nowrap'
                      records={hierarchicalData}
                      columns={columns(estimationModelGroup)}
                      rowStyle={(row) => {
                        const estimationItem = config.getEstimationItemForVisibility(row);
                        const isVisible = estimationItem ? shouldShowItem(estimationItem) : true;
                        return {
                          backgroundColor: !row.isParent ? 'rgba(0, 0, 0, 0.08)' : undefined,
                          fontWeight: row.isParent ? 'bold' : 'normal',
                          visibility: isVisible ? 'visible' : 'collapse',
                        };
                      }}
                    />
                  </div>
                </MantineProvider>
              </div>
            );
          })}
      </Stack>
    </div>
  );
};

export default HierarchicalManagementReportTable;
